import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Shield, AtSign, CheckCircle2 } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user?.email && !user.discord_only) setEmail(user.email);
  }, [user]);

  if (!user) {
    return <div className="p-8 text-center text-gray-500 font-mono uppercase tracking-widest text-xs">Log in to view your profile.</div>;
  }

  const attach = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/me/attach-email", { email });
      setSent(true);
      toast.success("Confirmation email sent — check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send confirmation email");
    } finally {
      setBusy(false);
    }
  };

  const isDiscordOnly = user.discord_only || (user.email || "").endsWith("@legacyhll.local");

  return (
    <div className="max-w-2xl mx-auto py-12 space-y-8" data-testid="profile-root">
      <div>
        <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-3">/ ACCOUNT</div>
        <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Profile</h1>
        <p className="text-sm text-gray-400 mt-2">Manage how we identify and reach you.</p>
      </div>

      {/* Identity card */}
      <div className="border border-[#232330] bg-[#101015] p-6 space-y-4" data-testid="profile-identity">
        <div className="flex items-center gap-3">
          <Shield className="w-4 h-4 text-cyan-400" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Signed in as</div>
            <div className="font-heading text-2xl font-black uppercase tracking-tight">{user.name || "Commander"}</div>
          </div>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest border border-[#232330] px-2 py-1 text-gray-400">{user.role}</span>
        </div>
        <div className="grid gap-3 md:grid-cols-2 text-sm">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Email</div>
            <div className="font-mono text-white break-all" data-testid="profile-email">
              {isDiscordOnly ? <span className="text-gray-500 italic">— not linked yet —</span> : user.email}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Discord</div>
            <div className="font-mono text-[#5865F2]" data-testid="profile-discord">
              {user.discord_id ? `#${user.discord_id.slice(-6)}` : "— not linked —"}
            </div>
          </div>
        </div>
      </div>

      {/* Attach email card */}
      {isDiscordOnly && (
        <div className="border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-6 space-y-4" data-testid="profile-attach-card">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-[#F4D03F]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">Attach a real email</div>
              <div className="text-sm text-gray-300 mt-1">
                You signed in with Discord and don't have an email on file. Add one so we can send you password reset links and match notifications.
              </div>
            </div>
          </div>
          {sent ? (
            <div data-testid="profile-attach-sent" className="border border-emerald-400/40 bg-emerald-400/5 p-4 flex gap-3 items-start">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />
              <div className="text-sm">
                <div className="text-emerald-400 font-mono uppercase tracking-widest text-xs font-bold">Check your inbox</div>
                <div className="text-gray-300 mt-1">
                  Confirmation link sent to <span className="font-mono text-cyan-400">{email}</span>. It expires in 60 minutes.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={attach} className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-widest text-gray-400">Email</Label>
                <Input
                  data-testid="profile-attach-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-none bg-[#05050A] border-[#232330] mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="profile-attach-submit"
                className="md:self-end rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black font-bold uppercase tracking-widest flex items-center gap-2"
              >
                <AtSign className="w-4 h-4" /> {busy ? "Sending…" : "Send confirmation"}
              </Button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
